/**
 * useRealtimeUpdates Hook
 *
 * Provides real-time notifications via SSE (Server-Sent Events) with automatic
 * fallback to polling when SSE is unavailable.
 *
 * When an SSE connection is active, all registered query keys
 * are invalidated based on server-pushed events — eliminating the
 * need for per-component refetchInterval polling.
 *
 * Usage:
 *   const { connectionMode, isConnected } = useRealtimeUpdates();
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { queryClient } from '@/lib/query-client';
import { captureError } from '@/lib/error-reporting';

type ConnectionMode = 'sse' | 'polling' | 'connecting';

interface RealtimeUpdatesState {
  /** Current connection strategy */
  connectionMode: ConnectionMode;
  /** Whether we have an active SSE connection */
  isConnected: boolean;
}

// Read client-side poll interval from Vite env (default: 30s)
const POLL_INTERVAL_MS = parseInt(
  import.meta.env.VITE_EMAIL_POLL_INTERVAL_MS || '30000',
  10,
);

/**
 * Map of SSE event types to query keys that should be invalidated.
 * The server broadcasts these event types; the client invalidates matching queries.
 */
const EVENT_QUERY_MAP: Record<string, string[]> = {
  'new-emails': ['/api/gmail/emails/recent'],
  'chat-update': ['/api/chat/unread-count'],
  'chat:message': ['/api/chat/spaces', '/api/chat/unread-count'],
  'notification:new': ['/api/notifications', '/api/notifications/unread-count'],
  'activity-update': ['/api/dashboard/recent-activity'],
  'data-update': [
    '/api/dashboard/kpis',
    '/api/dashboard/status-distribution',
    '/api/dashboard/recent-activity',
    '/api/chat/unread-count',
    '/api/gmail/emails/recent',
  ],
};

/**
 * All query keys that should be polled when SSE is unavailable.
 */
const POLLING_QUERY_KEYS = [
  '/api/gmail/emails/recent',
  '/api/chat/unread-count',
  '/api/dashboard/recent-activity',
  '/api/notifications',
  '/api/notifications/unread-count',
];

interface RealtimeUpdatesOptions {
  /** Called when a notification:new SSE event arrives. */
  onNotification?: (data: { title?: string; message?: string; notification?: { title?: string; message?: string } }) => void;
}

export function useRealtimeUpdates(options: RealtimeUpdatesOptions = {}): RealtimeUpdatesState {
  const { onNotification } = options;
  const [state, setState] = useState<RealtimeUpdatesState>({
    connectionMode: 'connecting',
    isConnected: false,
  });

  // Keep the latest callback in a ref so the SSE effect never has to depend on
  // it. Callers commonly pass an inline function that is a new reference every
  // render; depending on it would tear down and reopen the connection on each
  // render (a reconnect storm). The ref lets us always invoke the newest
  // callback without re-running the effect.
  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    setState({ connectionMode: 'polling', isConnected: false });
    pollTimerRef.current = setInterval(() => {
      for (const key of POLLING_QUERY_KEYS) queryClient.invalidateQueries({ queryKey: [key] });
    }, POLL_INTERVAL_MS);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  }, []);

  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The single live EventSource. Held in a ref (not a local in connectSSE) so
  // that reconnects and unmount always close the *current* connection rather
  // than only the first one — otherwise every failed reconnect leaks a socket.
  const esRef = useRef<EventSource | null>(null);

  const connectSSE = useCallback(() => {
    // Close any existing connection before opening a new one so a reconnect
    // never leaves an orphaned EventSource behind.
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const es = new EventSource('/api/sse/email', { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      retryCountRef.current = 0;
      stopPolling();
      setState({ connectionMode: 'sse', isConnected: true });
    };

    es.onmessage = (event) => {
      // A successfully delivered message proves the connection is healthy, so
      // reset the backoff counter — the next disconnect starts from 1s again.
      retryCountRef.current = 0;
      try {
        const payload = JSON.parse(event.data);
        const keys = EVENT_QUERY_MAP[payload.type];
        if (keys) for (const k of keys) queryClient.invalidateQueries({ queryKey: [k] });
        if (payload.type === 'notification:new') {
          onNotificationRef.current?.(payload.data ?? payload);
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      // Close immediately to prevent browser's native auto-retry.
      es.close();
      if (esRef.current === es) esRef.current = null;
      setState({ connectionMode: 'connecting', isConnected: false });
      startPolling();
      captureError(new Error('SSE connection closed: /api/sse/email'));

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s … cap at 30s.
      const delay = Math.min(1000 * 2 ** retryCountRef.current, 30_000);
      retryCountRef.current += 1;

      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        connectSSE();
      }, delay);
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    connectSSE();
    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      stopPolling();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [connectSSE, stopPolling]);

  return state;
}
